/**
 * configMigration — periodic migration manager for .ateliercode/ project configs.
 *
 * On startup (and every 10 minutes), checks all projects to ensure each has
 * a .ateliercode/config.json + runners.json in its repo. If missing:
 *   1. Attempt to migrate from the legacy settings blob in the database.
 *   2. If that fails (missing data, I/O error), initialize with defaults.
 *
 * Once a project is validated as having the files, it's marked "migrated"
 * in memory so the system stops re-checking it. The set resets on app restart
 * so we re-validate at least once per session.
 */
import { logger } from './logger';
import {
  getProjectTerminalSettings,
  getProjectRunnerSettings,
  type AteliercodeConfig,
  type AteliercodeRunners,
} from './projectSettings';
import type { IBackend } from '@/services/backend/IBackend';
import type { Project } from '@/services/backend/types';

const TAG = 'ConfigMigration';
const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/** Set of project IDs that have been validated as migrated this session. */
const migratedProjects = new Set<string>();

let intervalHandle: ReturnType<typeof setInterval> | null = null;
let backendRef: IBackend | null = null;

/**
 * Run a single migration sweep across all projects.
 */
async function runMigrationSweep(): Promise<void> {
  if (!backendRef) return;
  const backend = backendRef;

  let projects: Project[];
  try {
    projects = await backend.projects.list();
  } catch (e) {
    logger.warn(TAG, 'Failed to list projects for migration check', e);
    return;
  }

  const activeProjects = projects.filter((p) => p.status === 'active');

  for (const project of activeProjects) {
    if (migratedProjects.has(project.id)) continue;

    try {
      await migrateProject(backend, project);
      migratedProjects.add(project.id);
    } catch (e) {
      logger.warn(TAG, `Migration failed for project ${project.id} (${project.name})`, e);
      // Will retry on next sweep
    }
  }
}

/**
 * Ensure a single project has .ateliercode/config.json + runners.json.
 *
 * Flow:
 *   1. Check if runners.json exists → if yes, project is already migrated.
 *   2. If not, try to migrate from settings blob.
 *   3. If blob has no useful data, create defaults.
 */
async function migrateProject(backend: IBackend, project: Project): Promise<void> {
  // Check if runners.json already exists
  const existingRunners = await backend.files.readAteliercodeRunners(project.id);
  if (existingRunners) {
    // Already has runners.json — check config.json too
    const existingConfig = await backend.files.readAteliercodeConfig(project.id);
    if (existingConfig) {
      logger.debug(TAG, `Project ${project.name}: already migrated`);
      return;
    }
    // runners exists but no config — create config from blob or defaults
    const config = buildConfigFromBlob(project);
    await backend.files.initAteliercodeConfig(
      project.id,
      JSON.stringify(config, null, 2),
      existingRunners, // keep existing runners as-is
    );
    logger.info(TAG, `Project ${project.name}: created config.json (runners.json already existed)`);
    return;
  }

  // No runners.json — attempt migration from blob
  const blobRunnerSettings = getProjectRunnerSettings(project.settings);
  const blobTermSettings = getProjectTerminalSettings(project.settings);

  let runners: AteliercodeRunners;
  let config: AteliercodeConfig;

  if (blobRunnerSettings.configs.length > 0) {
    // Migrate from blob
    logger.info(TAG, `Project ${project.name}: migrating ${blobRunnerSettings.configs.length} configs from settings blob`);
    runners = {
      $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
      configs: blobRunnerSettings.configs.map((c) => ({ name: c.name, command: c.command })),
      default: blobRunnerSettings.configs.find((c) => c.id === blobRunnerSettings.defaultConfigId)?.name ?? null,
    };
    config = {
      $schema: 'https://ateliercode.dev/schemas/config.v1.json',
      agent: {
        type: project.agent_type,
        autoLaunch: blobTermSettings.autoLaunchAgent,
        flags: blobTermSettings.agentFlags,
      },
      terminal: {
        defaultShell: blobTermSettings.defaultShell,
        notificationSound: blobTermSettings.notificationSound,
      },
    };
  } else {
    // No blob data — init with defaults
    logger.info(TAG, `Project ${project.name}: no legacy configs, initializing defaults`);
    runners = {
      $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
      configs: [],
      default: null,
    };
    config = buildConfigFromBlob(project);
  }

  await backend.files.initAteliercodeConfig(
    project.id,
    JSON.stringify(config, null, 2),
    JSON.stringify(runners, null, 2),
  );
  logger.info(TAG, `Project ${project.name}: .ateliercode/ initialized`);
}

/**
 * Build a config.json from whatever's in the project's settings blob,
 * falling back to sensible defaults for any missing fields.
 */
function buildConfigFromBlob(project: Project): AteliercodeConfig {
  const termSettings = getProjectTerminalSettings(project.settings);
  return {
    $schema: 'https://ateliercode.dev/schemas/config.v1.json',
    agent: {
      type: project.agent_type || 'claude',
      autoLaunch: termSettings.autoLaunchAgent,
      flags: termSettings.agentFlags,
    },
    terminal: {
      defaultShell: termSettings.defaultShell,
      notificationSound: termSettings.notificationSound,
    },
  };
}

/**
 * Start the periodic migration manager. Call once at app startup.
 * Safe to call multiple times — only the first call starts the interval.
 */
export function startConfigMigration(backend: IBackend): void {
  if (intervalHandle) return; // already running
  backendRef = backend;

  // Run first sweep immediately (async, fire-and-forget)
  runMigrationSweep().catch((e) =>
    logger.warn(TAG, 'Initial migration sweep failed', e),
  );

  // Schedule periodic sweeps
  intervalHandle = setInterval(() => {
    runMigrationSweep().catch((e) =>
      logger.warn(TAG, 'Periodic migration sweep failed', e),
    );
  }, CHECK_INTERVAL_MS);

  logger.info(TAG, 'Migration manager started (checks every 10 min)');
}

/**
 * Stop the periodic migration manager (e.g., on app unmount).
 */
export function stopConfigMigration(): void {
  if (intervalHandle) {
    clearInterval(intervalHandle);
    intervalHandle = null;
  }
  backendRef = null;
}

/**
 * Force a migration check for a specific project (e.g., after creation).
 * Marks it as migrated on success so the periodic sweep skips it.
 */
export async function ensureProjectMigrated(backend: IBackend, project: Project): Promise<void> {
  if (migratedProjects.has(project.id)) return;
  await migrateProject(backend, project);
  migratedProjects.add(project.id);
}
